/**
 * DictionariesTab — F12.N T5.3, przepisane w Etapie 3b na kontrakt SCALONY
 * (lokalny tenanta + globalny katalog ITE, `apps/api/lib/dictionaries/merge.ts`).
 *
 * Master-detail UI dla katalogu słowników tenanta:
 *   - Lista SCALONA (lokalne + opublikowane globalne nieprzesłonięte) — kolumny:
 *     nazwa / kod / pochodzenie (lokalny/systemowy/globalny) / # wartości / akcje.
 *   - Akcje per wiersz ZALEŻNE od originu + isSystem:
 *       local-custom  (origin=local, isSystem=false)  → Edytuj + Usuń
 *       local-system  (origin=local, isSystem=true)   → zablokowane (badge „systemowy")
 *       global        (origin=global, readonly=true)  → „Kopiuj do lokalnego" (POST .../copy)
 *   - Klik na wiersz → widok szczegółowy z pozycjami:
 *       local  → zwykłe CRUD pozycji (jak dotychczas)
 *       global → tylko przełącznik widoczności per pozycja (hide/unhide,
 *                POST/DELETE .../hidden-items) — meta i pozycje nieedytowalne
 *                bezpośrednio (trzeba najpierw skopiować do lokalnego).
 *   - „Przywróć wg kodu" — brak endpointu listującego skasowane lokalne słowniki
 *     (backend nie ma trybu `include_deleted` na `GET /dictionaries`, w
 *     przeciwieństwie do `document-types`), więc przywracanie działa przez modal
 *     z polem kodu → `POST /dictionaries/:code/restore` (resolvuje najświeższy
 *     soft-deleted wiersz po kodzie, case-insensitive).
 *
 * Gate: `crm_core.dictionaries.view` (widoczność listy) / `crm_core.dictionaries.manage`
 * (wszystkie mutacje — create/edit/delete/restore/copy/hide). Super-user bypassuje
 * (wzorzec z `DocumentTypesTab`: `me.permissions.includes(...)` + `isSuperUser`).
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.confirm/alert — ConfirmDialog + useToast.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  ArrowLeft,
  Copy,
  Eye,
  EyeOff,
  Globe,
  Info,
  Lock,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfigurableTable } from '@/components/configurable-table/ConfigurableTable';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { Tooltip } from '@/components/Tooltip';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  type CreateDictionaryInput,
  type DictionaryItem,
  type DictionaryParam,
  type DictionarySummary,
  type UpdateDictionaryInput,
  useDictionariesList,
  useDictionary,
  useDictionaryMutations,
  useDictionarySource,
} from '@/hooks/useDictionary';
import { useActiveCompanyScope } from '@/lib/use-active-company';

import { DictionaryFormModal, type DictionaryFormValues } from './DictionaryFormModal';
import { DictionaryItemFormModal, type DictionaryItemFormValues } from './DictionaryItemFormModal';

const VIEW_PERM = 'crm_core.dictionaries.view';
const MANAGE_PERM = 'crm_core.dictionaries.manage';
const MANAGE_SQL_PERM = 'crm_core.dictionaries.manage_sql';

// Stabilna pusta tablica parametrów — używana jako default, gdy detal słownika SQL jeszcze się
// nie załadował. `?? []` inline tworzyłby NOWĄ referencję na każdym renderze, przez co `useMemo`
// budujący `dictFormInitialValues` odtwarzałby obiekt co render → reset pól modala w złym momencie
// (dokładnie regresja z TC11/TC12). Stała referencja = Object.is stabilne w dep-arrayu.
const EMPTY_DICTIONARY_PARAMS: DictionaryParam[] = [];

/**
 * Mapuje kod błędu zapisu słownika SQL (sprzężenie źródła + walidacja strukturalna z backendu)
 * na czytelny komunikat pokazywany inline przy polu zapytania. Zwraca `null` dla kodów spoza tej
 * klasy (np. `CODE_TAKEN`) — caller obsługuje je osobno. `paramName` dochodzi dla
 * `SQL_PARAM_UNDECLARED`.
 */
function sqlConfigErrorMessage(
  code: string | null | undefined,
  paramName: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  switch (code) {
    case 'SOURCE_SQL_REQUIRED':
      return t('tenant.dictionaries.form.errors.sqlRequired');
    case 'SQL_NOT_SELECT':
      return t('tenant.dictionaries.form.sqlErrors.notSelect');
    case 'SQL_MULTIPLE_STATEMENTS':
      return t('tenant.dictionaries.form.sqlErrors.multipleStatements');
    case 'SQL_PARAM_UNDECLARED':
      return t('tenant.dictionaries.form.sqlErrors.paramUndeclared', {
        paramName: paramName ?? '',
      });
    case 'SQL_EMPTY':
      return t('tenant.dictionaries.form.sqlErrors.empty');
    case 'SQL_FORBIDDEN_KEYWORD':
      return t('tenant.dictionaries.form.sqlErrors.forbiddenKeyword');
    case 'SQL_UNSUPPORTED_SYNTAX':
      return t('tenant.dictionaries.form.sqlErrors.unsupportedSyntax');
    case 'SOURCE_SQL_NOT_ALLOWED':
    case 'SOURCE_PARAMS_NOT_ALLOWED':
      return t('tenant.dictionaries.form.sqlErrors.generic');
    default:
      return null;
  }
}

// ─── Główny komponent ──────────────────────────────────────────────────────

export function DictionariesTab() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  // Deep-link `?dict=<kod>` — wejście z zewnątrz wprost na zawartość konkretnego słownika
  // (dwuklik na węźle słownikowym w Planie kont). Param czytamy jako WARTOŚĆ POCZĄTKOWĄ,
  // nie jako źródło prawdy: dalsze przełączanie na liście ma zostawiać URL w spokoju.
  const [searchParams] = useSearchParams();
  const [selectedCode, setSelectedCode] = useState<string | null>(
    () => searchParams.get('dict') || null,
  );

  const isSuperUser = me?.user.isSuperUser === true;
  const canView = isSuperUser || (me?.permissions.includes(VIEW_PERM) ?? false);
  const canManage = isSuperUser || (me?.permissions.includes(MANAGE_PERM) ?? false);
  // Autorstwo SQL = osobne, wyższe uprawnienie (tenant pisze zapytanie wykonywane na jego
  // bazie). Bramkuje sekcję SQL w modalu ORAZ edycję/usunięcie słownika SQL na liście.
  const canManageSql = isSuperUser || (me?.permissions.includes(MANAGE_SQL_PERM) ?? false);

  if (!canView && !canManage) {
    return (
      <Card padding="md" data-testid="dictionaries-tab-no-permission">
        <p className="text-sm text-text-tertiary">{t('tenant.dictionaries.list.noPermission')}</p>
      </Card>
    );
  }

  // Master-detail dwupanelowy (jak panel ITE „Globalne słowniki"): lewa kolumna —
  // lista słowników (wybieralna), prawa — pozycje wybranego słownika. Zastępuje dawny
  // model „lista → wejdź w szczegół → wróć" (ekran się przełączał w całości).
  return (
    <div
      className="grid min-h-0 flex-1 grid-cols-1 gap-6 lg:h-full lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]"
      data-testid="dictionaries-tab"
    >
      <DictionariesList
        canManage={canManage}
        canManageSql={canManageSql}
        selectedCode={selectedCode}
        onSelect={(code) => setSelectedCode(code)}
      />
      <div className="flex min-h-0 flex-col">
        {selectedCode === null ? (
          <Card padding="md" data-testid="dictionaries-select-prompt">
            <p className="text-sm text-text-tertiary">
              {t('tenant.dictionaries.list.selectPrompt')}
            </p>
          </Card>
        ) : (
          <DictionaryDetailView
            code={selectedCode}
            canManage={canManage}
            onBack={() => setSelectedCode(null)}
          />
        )}
      </div>
    </div>
  );
}

// ─── Lista słowników ────────────────────────────────────────────────────────

function originBadge(
  d: Pick<DictionarySummary, 'origin' | 'isSystem' | 'sourceKind' | 'source'>,
  t: (key: string) => string,
) {
  if (d.origin === 'global') {
    return (
      <div className="flex items-center gap-1">
        <Badge color="blue" data-testid="dictionary-origin-badge-global">
          <Globe className="mr-1 inline h-3 w-3" />
          {t('tenant.dictionaries.list.originGlobal')}
        </Badge>
        {/* Etap 10 (ADR-0066): słownik globalny zasilany z zarejestrowanego źródła
            zewnętrznego — read-only wykraczające poza zwykły globalny (patrz gates
            w DictionariesList/DictionaryDetailView niżej). */}
        {d.sourceKind === 'external' && (
          <Badge color="amber" data-testid="dictionary-origin-badge-external">
            {t('tenant.dictionaries.list.originExternal')}
          </Badge>
        )}
      </div>
    );
  }
  // Faza 4 (ADR-0069): słownik LOKALNY zaimportowany syncem agenta ERP — badge „Symfonia"
  // PRZED „systemowy" (słownik ERP ma oba flagi: source='erp' ORAZ is_system=true).
  const erpBadge =
    d.source === 'erp' ? (
      <Badge color="blue" data-testid="dictionary-origin-badge-erp">
        {t('tenant.dictionaries.list.erpBadge')}
      </Badge>
    ) : null;
  if (d.isSystem) {
    return (
      <div className="flex items-center gap-1">
        {erpBadge}
        <Badge color="gray" data-testid="dictionary-origin-badge-system">
          <Lock className="mr-1 inline h-3 w-3" />
          {t('tenant.dictionaries.list.systemBadge')}
        </Badge>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      {erpBadge}
      <Badge color="accent" data-testid="dictionary-origin-badge-local">
        {t('tenant.dictionaries.list.customBadge')}
      </Badge>
      {/* Słownik LOKALNY z własnym zapytaniem SQL — na drucie `sourceKind='external'`, ale to
          nie katalog ITE (origin='local'). Osobny badge „SQL" odróżnia go od listy ręcznej. */}
      {d.sourceKind === 'external' && (
        <Badge color="amber" data-testid="dictionary-origin-badge-sql">
          {t('tenant.dictionaries.list.sqlBadge')}
        </Badge>
      )}
    </div>
  );
}

/**
 * Etykiety TYPU słownika jako tekst do wyszukiwarki — te same podpisy co odznaki
 * w kolumnie „Typ" (Globalny/Zewnętrzny/Systemowy/Własny/Symfonia), żeby dało się
 * filtrować listę po typie, nie tylko po nazwie/kodzie. Zwraca lowercase.
 */
function dictTypeSearchText(
  d: Pick<DictionarySummary, 'origin' | 'isSystem' | 'sourceKind' | 'source'>,
  t: (key: string) => string,
): string {
  const labels: string[] = [];
  if (d.origin === 'global') {
    labels.push(t('tenant.dictionaries.list.originGlobal'));
    if (d.sourceKind === 'external') labels.push(t('tenant.dictionaries.list.originExternal'));
  } else {
    if (d.source === 'erp') labels.push(t('tenant.dictionaries.list.erpBadge'));
    labels.push(
      d.isSystem
        ? t('tenant.dictionaries.list.systemBadge')
        : t('tenant.dictionaries.list.customBadge'),
    );
  }
  return labels.join(' ').toLowerCase();
}

/**
 * Odznaka POZIOMU POZYCJI (nie słownika) — odróżnia własną pozycję tenanta
 * (`origin==='extension'`) dopisaną do słownika globalnego od pozycji katalogu
 * ITE (`origin==='global'`) w tym samym widoku szczegółu. Celowo osobna od
 * `originBadge` powyżej (ta bierze `{origin,isSystem}` słownika — pozycje nie
 * mają `isSystem`).
 */
function itemOriginBadge(origin: DictionaryItem['origin'], t: (key: string) => string) {
  switch (origin) {
    case 'extension':
      return (
        <Badge color="accent" data-testid="dictionary-item-origin-badge-extension">
          {t('tenant.dictionaries.detail.extensionBadge')}
        </Badge>
      );
    case 'local':
    case 'global':
    case 'external':
    default:
      // Pozycje lokalne/globalne/zewnętrzne (Etap 10 — origin='external' dla pozycji
      // z zapytania do rejestru źródeł) nie dostają odznaki pochodzenia tutaj — tylko
      // własne rozszerzenia tenanta wyróżniamy wizualnie. `default` jest bezpiecznikiem
      // na przyszłe warianty `DictionaryOrigin`, których jeszcze nie znamy.
      return null;
  }
}

function DictionariesList({
  canManage,
  canManageSql,
  selectedCode,
  onSelect,
}: {
  canManage: boolean;
  canManageSql: boolean;
  selectedCode: string | null;
  onSelect: (code: string) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const mutations = useDictionaryMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DictionarySummary | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DictionarySummary | null>(null);
  const [pendingCopy, setPendingCopy] = useState<DictionarySummary | null>(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  // Błąd 409 (kod słownika zajęty) z ostatniej próby zapisu — pokazywany inline w
  // modalu (§4.12: nie tylko toast). Reset przy każdym otwarciu/zamknięciu modala
  // i tuż przed każdą próbą zapisu — patrz `handleCreate`.
  const [dictServerErrors, setDictServerErrors] = useState<Record<string, string>>({});

  const listQ = useDictionariesList();

  // Prefill treści zapytania przy edycji słownika SQL: lista NIE niesie `sourceSql` (sekret,
  // ujawniany tylko posiadaczowi `manage_sql`), więc dla słownika lokalnego zewnętrznego (= SQL)
  // doczytujemy zapisany tekst osobnym odczytem `.../source`. NIE z detalu: detal wykonuje
  // zapytanie i przy jego błędzie (503) nie niesie SQL-a, a właśnie takie zapytanie się poprawia.
  // Dla słownika ręcznego / w trybie tworzenia kod jest pusty → `enabled:false`, brak zapytania.
  const editingIsSql = editing?.sourceKind === 'external';
  const editingSource = useDictionarySource(editingIsSql ? editing.code : '');
  const editingSourceSql = editingSource.source?.sourceSql ?? '';
  const editingDeclaredParams = editingSource.source?.declaredParams ?? EMPTY_DICTIONARY_PARAMS;

  // Nie udało się wczytać zapytania → komunikat zamiast okna z pustym polem SQL; ponowny klik
  // „Edytuj" ponawia odczyt (wynik nie zostaje w cache'u). 404 = słownik usunięty lub już nie-SQL
  // (lista na ekranie jest nieaktualna, kolejny klik nic nie da) → odświeżamy listę; reszta błędów
  // (sieć, 5xx) może być przejściowa, więc komunikat zaprasza do ponowienia.
  const { refetch: refetchList } = listQ;
  useEffect(() => {
    if (!editingSource.isError) return;
    if (editingSource.errorStatus === 404) {
      toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.sourceNotFound') });
      void refetchList();
    } else {
      toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.sourceLoadError') });
    }
    setEditing(null);
  }, [editingSource.isError, editingSource.errorStatus, toast, t, refetchList]);

  // Stabilna referencja initialValues dla DictionaryFormModal — analogicznie do
  // `itemFormInitialValues` w `DictionaryDetailView` poniżej: inline literal
  // odtworzony na każdym renderze powodowałby, że efekt resetujący pola w modalu
  // (zależny od `[open, initialValues]`) odpalałby się przy KAŻDYM re-renderze
  // rodzica (np. gdy `isPending` mutacji przełącza się po nieudanym zapisie),
  // czyszcząc właśnie wpisany kod/nazwę dokładnie w momencie błędu 409.
  const dictFormInitialValues = useMemo<DictionaryFormValues>(
    () =>
      editing
        ? {
            code: editing.code,
            name: editing.name,
            description: editing.description ?? '',
            displayMode: editing.displayMode,
            sourceKind: editingIsSql ? 'sql' : 'manual',
            sourceSql: editingSourceSql,
            declaredParams: editingDeclaredParams,
          }
        : {
            code: '',
            name: '',
            description: '',
            displayMode: 'label',
            sourceKind: 'manual',
            sourceSql: '',
            declaredParams: [],
          },
    [editing, editingIsSql, editingSourceSql, editingDeclaredParams],
  );

  function handleCreate(values: DictionaryFormValues) {
    setDictServerErrors({});
    const input: CreateDictionaryInput = {
      code: values.code,
      name: values.name,
      description: values.description,
      displayMode: values.displayMode,
      // Pola źródła tylko dla wariantu SQL — słownik ręczny idzie bez nich (backend defaultuje).
      ...(values.sourceKind === 'sql'
        ? {
            sourceKind: 'sql' as const,
            sourceSql: values.sourceSql,
            declaredParams: values.declaredParams,
          }
        : {}),
    };
    mutations.createDictionary.mutate(input, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('tenant.dictionaries.toast.created') });
        setCreateOpen(false);
      },
      onError: (err) => {
        const data = isAxiosError(err)
          ? (err.response?.data as { error?: string; paramName?: string } | undefined)
          : null;
        const code = data?.error ?? null;
        if (code === 'CODE_TAKEN') {
          toast.show({ variant: 'warning', title: t('tenant.dictionaries.toast.codeTaken') });
          setDictServerErrors({ code: t('tenant.dictionaries.toast.codeTaken') });
          return;
        }
        const sqlMsg = sqlConfigErrorMessage(code, data?.paramName, t);
        if (sqlMsg) {
          toast.show({ variant: 'warning', title: sqlMsg });
          setDictServerErrors({ sourceSql: sqlMsg });
          return;
        }
        toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.createError') });
      },
    });
  }

  function handleUpdate(code: string, values: DictionaryFormValues) {
    setDictServerErrors({});
    // Pola źródła wysyłamy tylko gdy user MA uprawnienie autoringu SQL i realnie dotykamy SQL-a
    // (wariant SQL LUB konwersja z istniejącego słownika SQL na ręczny). Zwykła edycja metadanych
    // słownika ręcznego idzie bez pól źródła — inaczej backend policzyłby zbędny no-op source-touch.
    const sourcePatch =
      canManageSql && (values.sourceKind === 'sql' || editingIsSql)
        ? values.sourceKind === 'sql'
          ? {
              sourceKind: 'sql' as const,
              sourceSql: values.sourceSql,
              declaredParams: values.declaredParams,
            }
          : { sourceKind: 'manual' as const }
        : {};
    const updateValues: UpdateDictionaryInput = {
      name: values.name,
      description: values.description ?? null,
      displayMode: values.displayMode,
      ...sourcePatch,
    };
    mutations.updateDictionary.mutate(
      { code, values: updateValues },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.dictionaries.toast.updated') });
          setEditing(null);
        },
        onError: (err) => {
          const data = isAxiosError(err)
            ? (err.response?.data as { error?: string; paramName?: string } | undefined)
            : null;
          const sqlMsg = sqlConfigErrorMessage(data?.error ?? null, data?.paramName, t);
          if (sqlMsg) {
            toast.show({ variant: 'warning', title: sqlMsg });
            setDictServerErrors({ sourceSql: sqlMsg });
            return;
          }
          toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.updateError') });
        },
      },
    );
  }

  function handleDelete(d: DictionarySummary) {
    mutations.deleteDictionary.mutate(d.code, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('tenant.dictionaries.toast.deleted') });
        setPendingDelete(null);
      },
      onError: (err) => {
        const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
        if (code === 'SYSTEM_PROTECTED') {
          toast.show({ variant: 'warning', title: t('tenant.dictionaries.toast.systemProtected') });
          setPendingDelete(null);
          return;
        }
        toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.deleteError') });
        setPendingDelete(null);
      },
    });
  }

  function handleCopy(d: DictionarySummary) {
    mutations.copyGlobalToLocal.mutate(d.code, {
      onSuccess: () => {
        toast.show({
          variant: 'success',
          title: t('tenant.dictionaries.toast.copied'),
          description: t('tenant.dictionaries.toast.copyHint'),
        });
        setPendingCopy(null);
      },
      onError: (err) => {
        const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
        if (code === 'CODE_TAKEN') {
          toast.show({ variant: 'warning', title: t('tenant.dictionaries.toast.codeTaken') });
          setPendingCopy(null);
          return;
        }
        toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.copyError') });
        setPendingCopy(null);
      },
    });
  }

  function handleRestore(code: string) {
    mutations.restoreDictionary.mutate(code, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('tenant.dictionaries.toast.restored') });
        setRestoreOpen(false);
      },
      onError: (err) => {
        const errCode = isAxiosError(err)
          ? (err.response?.data?.error as string | undefined)
          : null;
        if (errCode === 'CODE_TAKEN') {
          toast.show({
            variant: 'warning',
            title: t('tenant.dictionaries.toast.restoreCodeTaken'),
          });
          return;
        }
        if (errCode === 'NOT_FOUND') {
          toast.show({ variant: 'warning', title: t('tenant.dictionaries.toast.restoreNotFound') });
          return;
        }
        toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.restoreError') });
      },
    });
  }

  // Zbiór rodzajów słowników obecnych w danych — do podpowiedzi w filtrze kolumny „Typ".
  const kindFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const d of listQ.data ?? []) {
      const label = dictTypeSearchText(d, t);
      if (!seen.has(label)) seen.set(label, label);
    }
    return [...seen.keys()]
      .sort((a, b) => a.localeCompare(b, 'pl'))
      .map((v) => ({ value: v, label: v }));
  }, [listQ.data, t]);

  // Kolumny listy słowników — każda filtrowalna (wiersz filtrów pod nagłówkiem).
  const dictColumns = useMemo<ColumnDefinition<DictionarySummary>[]>(
    () => [
      {
        id: 'name',
        // Filtr kolumny szuka po NAZWIE — kod jest tu tylko wizualnym chipem (patrz
        // renderCell), nie osobną filtrowalną kolumną (usunięta, dublowała informację
        // z nagłówkiem oddalonym od nazwy o cały wiersz — feedback z przeglądu).
        accessor: (d) => d.name,
        header: t('tenant.dictionaries.list.col.name'),
        // Szerokości PROCENTOWE, nie pikselowe: tabela ma układ automatyczny, więc piksele
        // są tylko sugestią — wolne miejsce i tak rozjeżdżało wąską kolumnę z liczbą do
        // ~200 px. Proporcje sumują się do 100%, więc nazwa (najdłuższa treść) bierze slack,
        // a liczba zostaje wąska.
        defaultWidth: '65%',
        sortable: true,
        filterable: true,
        headerFilter: { category: 'text' },
        // Opis bywa długi (kilka zdań o źródle danych) i rozpychał wiersz na pół ekranu.
        // `truncate` = jedna linia z WIDOCZNYM wielokropkiem (`line-clamp-2` ucinał tekst
        // bez żadnego znaku, więc nie było widać, że coś jeszcze jest). Pełną treść pokazuje
        // NASZ Tooltip (Radix, portal, stylowany motywem) — nie systemowa dymka `title`,
        // która ignoruje motyw i wygląda obco. Nazwa `break-words` — długi kod bez spacji
        // nie rozjedzie kolumny.
        renderCell: (_v, d) => (
          <div className="max-w-[360px]">
            <div className="flex items-center gap-2">
              <span className="break-words font-medium text-text-primary">{d.name}</span>
              <code className="shrink-0 rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-secondary">
                {d.code}
              </code>
            </div>
            {d.description && (
              <Tooltip content={d.description} placement="top" maxWidth={420}>
                <div className="truncate text-xs text-text-tertiary">{d.description}</div>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        id: 'itemCount',
        accessor: (d) => d.itemCount,
        header: t('tenant.dictionaries.list.col.items'),
        // Kolumna trzyma jedną liczbę — szeroka rozpychała tabelę i zabierała miejsce
        // nazwie. `maxWidth` trzyma ją wąsko także gdy tabela ma wolną przestrzeń.
        defaultWidth: '10%',
        align: 'right',
        sortable: true,
        filterable: true,
        headerFilter: { category: 'number' },
      },
      {
        id: 'kind',
        // Akcesor zwraca tekst rodzaju (globalny/lokalny/SQL/ERP) — ten sam, po którym
        // filtrowało dawne pole „szukaj", więc filtr kolumny działa po ludzkiej nazwie.
        accessor: (d) => dictTypeSearchText(d, t),
        header: t('tenant.dictionaries.list.col.kind'),
        defaultWidth: '25%',
        filterable: true,
        // `select` + JAWNE `options` — popup podpowiada wartości bez pytania serwera.
        // Bez `options` zakładka „Wartości" próbuje dociągnąć podpowiedzi endpointem
        // `/distinct` per encja, którego ten ekran nie ma (dane są klienckie) i pokazywała
        // „Brak wartości". Listę składamy z tego, co realnie jest na liście słowników.
        headerFilter: { category: 'select', options: kindFilterOptions },
        renderCell: (_v, d) => originBadge(d, t),
      },
    ],
    [t, kindFilterOptions],
  );

  // Akcje wiersza słownika — kopiowanie globalnego do lokalnego oraz edycja/usunięcie
  // lokalnego. Bramkowanie 1:1 jak w dawnej tabeli: słownik systemowy i ERP bez akcji,
  // słownik SQL wymaga uprawnienia autoringu SQL, nie zwykłego `manage`.
  function renderDictActions(d: DictionarySummary) {
    return (
      <div className="flex items-center justify-end gap-1">
        {canManage && d.origin === 'global' && (
          <button
            type="button"
            disabled={d.sourceKind === 'external'}
            onClick={() => setPendingCopy(d)}
            aria-label={t('tenant.dictionaries.list.copyToLocal')}
            title={
              d.sourceKind === 'external'
                ? t('tenant.dictionaries.detail.externalReadonlyHint')
                : undefined
            }
            className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-muted"
            data-testid={`dictionary-copy-${d.code}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
        {(d.sourceKind === 'external' ? canManageSql : canManage) &&
          d.origin === 'local' &&
          !d.isSystem &&
          d.source !== 'erp' && (
            <>
              <button
                type="button"
                onClick={() => {
                  setDictServerErrors({});
                  setEditing(d);
                }}
                aria-label={t('common.edit')}
                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                data-testid={`dictionary-edit-${d.code}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setPendingDelete(d)}
                aria-label={t('common.delete')}
                className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                data-testid={`dictionary-delete-${d.code}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
      </div>
    );
  }

  if (listQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const allDicts = listQ.data ?? [];

  // Wybrany słownik ZAWSZE pierwszy. Lista jest długa i przewijana niezależnie od panelu
  // pozycji — bez tego po wyborze słownika z dołu listy tracisz go z oczu przy scrollowaniu
  // pozycji i nie widzisz, czyje pozycje właściwie oglądasz. `slice()` bo `sort` mutuje.
  // Filtrowanie robi już tabela (filtry pod nagłówkami kolumn) — osobnej szukajki nie ma.
  const dicts =
    selectedCode === null
      ? allDicts
      : allDicts
          .slice()
          .sort((a, b) => (a.code === selectedCode ? -1 : b.code === selectedCode ? 1 : 0));

  return (
    <div className="flex min-h-0 flex-col" data-testid="dictionaries-list-pane">
      <header className="mb-3 flex shrink-0 items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.dictionaries.list.title')}
          </h2>
          <Tooltip
            content={t('tenant.dictionaries.list.subtitle')}
            placement="bottom"
            maxWidth={320}
          >
            <span
              tabIndex={0}
              className="inline-flex cursor-help text-text-muted hover:text-text-secondary"
              aria-label={t('tenant.dictionaries.list.subtitle')}
            >
              <Info className="h-4 w-4" aria-hidden="true" />
            </span>
          </Tooltip>
        </div>
        {canManage && (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="secondary"
              leftIcon={<RotateCcw className="h-4 w-4" />}
              onClick={() => setRestoreOpen(true)}
              data-testid="dictionaries-restore-btn"
            >
              {t('tenant.dictionaries.list.restoreByCode')}
            </Button>
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setDictServerErrors({});
                setCreateOpen(true);
              }}
              data-testid="dictionaries-new-btn"
            >
              {t('tenant.dictionaries.list.newDictionary')}
            </Button>
          </div>
        )}
      </header>

      {allDicts.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('tenant.dictionaries.list.empty')}</p>
        </Card>
      ) : (
        // `fillHeight` — ConfigurableTable sam robi JEDEN `overflow-auto` (obie osie)
        // ograniczony wysokością tej ramki (`min-h-0 flex-1` na Card), więc poziomy
        // pasek przewijania siedzi na dole WIDOCZNEGO panelu i jest zawsze widoczny
        // (wcześniej: dwa zagnieżdżone kontenery scrolla — zewnętrzny pionowy +
        // wewnętrzny ConfigurableTable poziomy — chowały poziomy pasek na dole CAŁEJ
        // treści, widoczny tylko po doscrollowaniu listy do końca).
        // Tabela konfigurowalna — filtr nad KAŻDĄ kolumną (nazwa/kod/liczba wartości/typ)
        // zamiast jednego pola „szukaj wszędzie". Dane klienckie → filtrowanie lokalne.
        // `testIdPrefix="dictionary"` daje wiersze `dictionary-row-<kod>` — ta sama kotwica
        // co w ręcznie pisanej tabeli, więc testy i smoke celują bez zmian.
        <div className="flex min-h-0 flex-1 flex-col">
          <ConfigurableTable<DictionarySummary>
            tableId="tenant-dictionaries"
            data={dicts}
            getRowId={(d) => d.code}
            columns={dictColumns}
            filterable
            configurable={false}
            persistConfig={false}
            // Kafelki/kanban nie mają tu sensu — to ekran ustawień, nie kartoteka.
            showViewModeToggle={false}
            stickyHeader
            compact
            fillHeight
            testIdPrefix="dictionary"
            onRowClick={(d) => onSelect(d.code)}
            // Aktywny słownik = samo TŁO zaznaczenia (token `surface-selected` #eef4ff), bez
            // lewego paska-borderu — border gryzł się z liniami nagłówka/wierszy tabeli
            // (decyzja Eugenii). Zaznaczenie czytelne, zero konkurencji z ramkami.
            rowClassName={(d) =>
              'cursor-pointer ' +
              (selectedCode === d.code ? 'bg-accent-50 font-medium dark:bg-accent-500/10' : '')
            }
            emptyTitle={t('tenant.dictionaries.list.noMatches')}
            renderActions={renderDictActions}
            actionsColumnWidth={96}
          />
        </div>
      )}

      {canManage && (
        <DictionaryFormModal
          // Edycja słownika SQL doczytuje treść zapytania (`editingSource`) — dopóki trwa,
          // `initialValues` niosłoby pusty SQL, a po załadowaniu zmiana referencji odpaliłaby
          // reset modala DRUGI raz, wymazując nagłówek wpisany w międzyczasie. Wstrzymujemy otwarcie
          // edycji SQL do załadowania treści, a przy błędzie nie otwieramy w ogóle (efekt wyżej
          // pokazuje komunikat). Create i edycja ręczna otwierają się natychmiast.
          open={
            createOpen ||
            (editing !== null &&
              !(editingIsSql && (editingSource.isLoading || editingSource.isError)))
          }
          mode={editing ? 'edit' : 'create'}
          initialValues={dictFormInitialValues}
          canManageSql={canManageSql}
          serverErrors={dictServerErrors}
          saving={mutations.createDictionary.isPending || mutations.updateDictionary.isPending}
          onSubmit={(values) => {
            if (editing) {
              handleUpdate(editing.code, values);
            } else {
              handleCreate(values);
            }
          }}
          onClose={() => {
            setCreateOpen(false);
            setEditing(null);
            setDictServerErrors({});
          }}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.dictionaries.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.dictionaries.confirmDelete.message', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) handleDelete(pendingDelete);
        }}
        onClose={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingCopy !== null}
        title={t('tenant.dictionaries.confirmCopy.title')}
        message={
          pendingCopy
            ? t('tenant.dictionaries.confirmCopy.message', { name: pendingCopy.name })
            : ''
        }
        confirmLabel={t('tenant.dictionaries.list.copyToLocal')}
        variant="primary"
        onConfirm={() => {
          if (pendingCopy) handleCopy(pendingCopy);
        }}
        onClose={() => setPendingCopy(null)}
      />

      {canManage && (
        <RestoreByCodeModal
          open={restoreOpen}
          saving={mutations.restoreDictionary.isPending}
          onSubmit={handleRestore}
          onClose={() => setRestoreOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Modal „Przywróć wg kodu" ───────────────────────────────────────────────

function RestoreByCodeModal({
  open,
  saving,
  onSubmit,
  onClose,
}: {
  open: boolean;
  saving?: boolean;
  onSubmit: (code: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.dictionaries.restoreModal.title')}
      description={t('tenant.dictionaries.restoreModal.description')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!code.trim()}
            onClick={() => onSubmit(code.trim())}
            data-testid="dictionary-restore-submit"
          >
            {t('tenant.dictionaries.restoreModal.submit')}
          </Button>
        </>
      }
    >
      <FormField label={t('tenant.dictionaries.restoreModal.codeLabel')}>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="np. project_status"
          data-testid="dictionary-restore-code-input"
        />
      </FormField>
    </Modal>
  );
}

// ─── Widok szczegółowy słownika ────────────────────────────────────────────

export function DictionaryDetailView({
  code,
  canManage,
  onBack,
}: {
  code: string;
  canManage: boolean;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const mutations = useDictionaryMutations();
  const activeCompanyScope = useActiveCompanyScope();

  const [createItemOpen, setCreateItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DictionaryItem | null>(null);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<DictionaryItem | null>(null);
  // Wyszukiwarka nad pozycjami słownika — filtr kliencki po kodzie/etykiecie.
  // Błąd 409 (kod pozycji zajęty) z ostatniej próby zapisu — pokazywany inline w
  // modalu (§4.12: nie tylko toast). Reset przy każdym otwarciu/zamknięciu modala
  // i tuż przed każdą próbą zapisu — patrz `handleCreateItem`.
  const [itemServerErrors, setItemServerErrors] = useState<Record<string, string>>({});

  const detailQ = useDictionary(code);

  // Stabilna referencja initialValues dla DictionaryItemFormModal — inline literal
  // odtworzony na każdym renderze powodowałby, że efekt resetujący pola w modalu
  // (zależny od `[open, initialValues]`) odpalałby się przy KAŻDYM re-renderze
  // rodzica (np. gdy `isPending` mutacji przełącza się po nieudanym zapisie),
  // czyszcząc właśnie wpisany kod/etykietę dokładnie w momencie błędu 409.
  const itemFormInitialValues = useMemo<DictionaryItemFormValues>(
    () =>
      editingItem
        ? {
            code: editingItem.code,
            label: editingItem.label,
            sortOrder: editingItem.sortOrder ?? 0,
            color: editingItem.color ?? '',
            value: editingItem.value ?? '',
          }
        : { code: '', label: '', sortOrder: 0, color: '', value: '' },
    [editingItem],
  );

  // Sortuj items wg sort_order (bez sortOrder na końcu). useMemo musi być
  // PRZED conditional returns żeby zachować Rules of Hooks.
  const items = useMemo(() => {
    const raw = detailQ.dictionary?.items ?? [];
    return [...raw].sort(
      (a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER),
    );
  }, [detailQ.dictionary?.items]);

  // Filtrowanie pozycji robi tabela (filtry pod nagłówkami kolumn) — osobnej szukajki
  // nad tabelą nie ma, bo dublowała tę samą funkcję w gorszej formie (jedno pole na
  // wszystkie kolumny).

  // Kolumny tabeli pozycji. Kolumna „widoczność" ma sens WYŁĄCZNIE w słowniku globalnym
  // (tam tenant ukrywa pozycje katalogu ITE) — w lokalnym nie ma czego ukrywać, więc jej
  // nie dokładamy. Każda kolumna filtrowalna → wiersz filtrów pod nagłówkiem.
  // UWAGA: `isGlobal` niżej powstaje PO wczesnych returnach, a hooki muszą odpalić się
  // bezwarunkowo — dlatego kolumny liczą origin wprost z zapytania.
  const columnsForGlobal = detailQ.dictionary?.origin === 'global';
  // Kolor ma sens tylko w słownikach, które go faktycznie używają (np. Grupy) — w reszt(np.
  // Kody GTU) KAŻDA pozycja ma `color: null`, więc kolumna byłaby samymi myślnikami i zajmowała
  // miejsce bez informacji (feedback z przeglądu). Sprawdzamy realne dane, nie kod/typ słownika —
  // odporne na przyszłe słowniki bez hardkodowanej listy wyjątków.
  const anyItemHasColor = useMemo(() => items.some((it) => !!it.color), [items]);
  const itemColumns = useMemo<ColumnDefinition<DictionaryItem>[]>(() => {
    const base: ColumnDefinition<DictionaryItem>[] = [
      {
        id: 'sortOrder',
        accessor: (it) => it.sortOrder,
        header: t('tenant.dictionaries.detail.col.sort'),
        // 80px ucinało nagłówek „Kolejność" (tekst + ikonki sortu/filtra w nagłówku nie
        // mieściły się, `overflow-hidden` obcinał słowo). Kolumna dociąga się do `maxWidth`
        // (rozkład szerokości w tabeli, nie tylko `defaultWidth`) — 100px to najwęższy
        // pułap, przy którym nagłówek się jeszcze mieści bez ucinania.
        defaultWidth: 95,
        maxWidth: 100,
        // Do lewej — jak wszystkie inne kolumny tej tabeli (feedback z przeglądu; poprzednio
        // jedyna kolumna wyrównana do prawej, wyglądała niespójnie).
        align: 'left',
        sortable: true,
        filterable: true,
        headerFilter: { category: 'number' },
        renderCell: (_v, it) => <span className="text-text-tertiary">{it.sortOrder ?? '—'}</span>,
      },
      {
        id: 'code',
        accessor: (it) => it.code,
        header: t('tenant.dictionaries.detail.col.code'),
        sortable: true,
        filterable: true,
        headerFilter: { category: 'text' },
        renderCell: (_v, it) => (
          <div className="flex items-center gap-1.5">
            <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs">{it.code}</code>
            {itemOriginBadge(it.origin, t)}
          </div>
        ),
      },
      {
        id: 'label',
        accessor: (it) => it.label,
        header: t('tenant.dictionaries.detail.col.label'),
        sortable: true,
        filterable: true,
        headerFilter: { category: 'text' },
        renderCell: (_v, it) => <span className="text-text-primary">{it.label}</span>,
      },
      ...(anyItemHasColor
        ? [
            {
              id: 'color',
              accessor: (it) => it.color ?? '',
              header: t('tenant.dictionaries.detail.col.color'),
              defaultWidth: 120,
              filterable: true,
              headerFilter: { category: 'text' },
              renderCell: (_v, it) =>
                it.color ? (
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-4 w-4 rounded border border-border-primary"
                      style={{ backgroundColor: it.color }}
                    />
                    <span className="text-xs text-text-tertiary">{it.color}</span>
                  </div>
                ) : (
                  <span className="text-xs text-text-tertiary">—</span>
                ),
            } satisfies ColumnDefinition<DictionaryItem>,
          ]
        : []),
    ];
    if (!columnsForGlobal) return base;
    return [
      ...base,
      {
        id: 'visibility',
        // Akcesor zwraca ETYKIETĘ stanu, nie boolean — filtr kolumny porównuje tekstowo,
        // więc po „ukryta" da się filtrować tak samo jak po dowolnej innej kolumnie.
        accessor: (it) =>
          it.isHidden
            ? t('tenant.dictionaries.detail.hiddenBadge')
            : t('tenant.dictionaries.detail.visibleBadge'),
        header: t('tenant.dictionaries.detail.col.visibility'),
        defaultWidth: 120,
        filterable: true,
        headerFilter: {
          category: 'select',
          options: [
            {
              value: t('tenant.dictionaries.detail.visibleBadge'),
              label: t('tenant.dictionaries.detail.visibleBadge'),
            },
            {
              value: t('tenant.dictionaries.detail.hiddenBadge'),
              label: t('tenant.dictionaries.detail.hiddenBadge'),
            },
          ],
        },
        renderCell: (_v, it) =>
          it.isHidden ? (
            <Badge color="gray" data-testid={`dictionary-item-hidden-badge-${it.code}`}>
              {t('tenant.dictionaries.detail.hiddenBadge')}
            </Badge>
          ) : (
            <Badge color="green">{t('tenant.dictionaries.detail.visibleBadge')}</Badge>
          ),
      },
    ];
  }, [columnsForGlobal, anyItemHasColor, t]);

  function handleCreateItem(values: DictionaryItemFormValues) {
    setItemServerErrors({});
    mutations.createItem.mutate(
      { code, values },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.dictionaries.toast.itemCreated') });
          setCreateItemOpen(false);
        },
        onError: (err) => {
          const errCode = isAxiosError(err)
            ? (err.response?.data?.error as string | undefined)
            : null;
          if (errCode === 'ITEM_CODE_TAKEN') {
            toast.show({ variant: 'warning', title: t('tenant.dictionaries.toast.itemCodeTaken') });
            setItemServerErrors({ code: t('tenant.dictionaries.toast.itemCodeTaken') });
            return;
          }
          toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.itemCreateError') });
        },
      },
    );
  }

  function handleUpdateItem(itemId: string, values: DictionaryItemFormValues) {
    mutations.updateItem.mutate(
      {
        code,
        itemId,
        // `code` immutable po stronie backendu — wysyłamy tylko zmienne pola.
        values: {
          label: values.label,
          sortOrder: values.sortOrder,
          color: values.color || null,
          value: values.value ?? null,
        },
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.dictionaries.toast.itemUpdated') });
          setEditingItem(null);
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.itemUpdateError') });
        },
      },
    );
  }

  function handleDeleteItem(itemId: string) {
    mutations.deleteItem.mutate(
      { code, itemId },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.dictionaries.toast.itemDeleted') });
          setPendingDeleteItem(null);
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.itemDeleteError') });
          setPendingDeleteItem(null);
        },
      },
    );
  }

  // Własna pozycja tenanta dopisana do słownika GLOBALNEGO (Etap 1b) — edycja/
  // usunięcie idzie przez `.../extensions/:itemId`, NIE `.../items/:itemId`
  // (ten drugi route zwróciłby 404 — pozycji nie ma w tabeli `dictionary_items`).
  function handleUpdateExtension(itemId: string, values: DictionaryItemFormValues) {
    mutations.updateExtension.mutate(
      {
        code,
        itemId,
        values: { label: values.label, sortOrder: values.sortOrder, color: values.color || null },
      },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.dictionaries.toast.itemUpdated') });
          setEditingItem(null);
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.itemUpdateError') });
        },
      },
    );
  }

  function handleDeleteExtension(itemId: string) {
    mutations.deleteExtension.mutate(
      { code, itemId },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('tenant.dictionaries.toast.itemDeleted') });
          setPendingDeleteItem(null);
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.itemDeleteError') });
          setPendingDeleteItem(null);
        },
      },
    );
  }

  function handleToggleHidden(item: DictionaryItem, hidden: boolean) {
    mutations.setItemHidden.mutate(
      { code, itemCode: item.code, hidden },
      {
        onSuccess: () => {
          toast.show({
            variant: 'success',
            title: hidden
              ? t('tenant.dictionaries.toast.itemHidden')
              : t('tenant.dictionaries.toast.itemUnhidden'),
          });
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('tenant.dictionaries.toast.itemHideError') });
        },
      },
    );
  }

  if (detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  // Etap 6 (frontend): słowniki zewnętrzne z parametrem `context.company` (Etap 10,
  // ADR-0066) wymagają aktywnej firmy — backend zwraca 400 MISSING_HEADER bez
  // `X-Company-Id`. W trybie "Wszystkie firmy" pokazujemy łagodny placeholder
  // zamiast generycznego błędu ładowania — scalenie tych danych między firmami
  // to odłożona decyzja produktowa, nie dług.
  if (activeCompanyScope.isAll && detailQ.error?.code === 'MISSING_HEADER') {
    return (
      <Card padding="md" data-testid="dictionary-detail-pick-company">
        <p className="text-sm text-text-tertiary">{t('common.pickCompanyForView')}</p>
        <Button variant="ghost" onClick={onBack} className="mt-3">
          <ArrowLeft className="mr-1 h-3 w-3" />
          {t('common.back')}
        </Button>
      </Card>
    );
  }

  if (!detailQ.dictionary) {
    return (
      <Card padding="md">
        <p className="text-sm text-error-500">{t('tenant.dictionaries.detail.loadError')}</p>
        <Button variant="ghost" onClick={onBack} className="mt-3">
          <ArrowLeft className="mr-1 h-3 w-3" />
          {t('common.back')}
        </Button>
      </Card>
    );
  }

  const dict = detailQ.dictionary;
  const isGlobal = dict.origin === 'global';
  // Etap 10 (ADR-0066): słownik globalny zasilany z zarejestrowanego źródła zewnętrznego —
  // pozycje pobierane na żywo, więc kopiowanie/rozszerzanie/ukrywanie jest zablokowane
  // (nie ma tu lokalnej reprezentacji do której dopisać/ukryć wiersz).
  const isExternal = dict.sourceKind === 'external';
  // Faza 4 (ADR-0069): słownik LOKALNY zaimportowany z ERP (Symfonia) — pozycje utrzymuje
  // sync agenta, edycja tenantowa zablokowana (backend: 409 ERP_SOURCE_PROTECTED; UI nie
  // powinno w ogóle proponować akcji).
  const isErp = dict.source === 'erp';
  // Słownik LOKALNY z własnym SQL (`origin='local'` + wire `sourceKind='external'`) — pozycje są
  // WYLICZANE zapytaniem, więc ręczna edycja/dodawanie pozycji jest zablokowane (bez `!isExternal`
  // widok pokazałby przyciski „Nowa wartość"/edycji na żywym zapytaniu). `isExternal` obejmuje
  // oba źródła zewnętrzne: globalny katalog ITE (isGlobal) i lokalny SQL tenanta.
  const isLocalSql = !isGlobal && isExternal;
  const canEditItems = canManage && !isGlobal && !isErp && !isExternal;
  const canToggleHidden = canManage && isGlobal;
  // Etap 1b: tenant może dopisać WŁASNE pozycje do słownika globalnego
  // (`POST .../items` — backend rozgałęzia na insert do `dictionary_item_extensions`
  // zamiast 409 GLOBAL_READONLY). Edycja/usunięcie tych pozycji idzie osobnym
  // route'em `.../extensions/:itemId` — patrz handleUpdateExtension/handleDeleteExtension.
  const canAddExtension = canManage && isGlobal;

  // Akcje wiersza pozycji — 1:1 przeniesione z poprzedniej, ręcznie pisanej tabeli
  // (ukrywanie pozycji globalnej, edycja/usunięcie pozycji lokalnej i rozszerzenia).
  // Bramkowanie bez zmian: słownik zewnętrzny i ERP pozostają tylko do odczytu.
  function renderItemActions(item: DictionaryItem) {
    return (
      <div className="flex items-center justify-end gap-1">
        {canToggleHidden && item.origin === 'global' && (
          <button
            type="button"
            disabled={isExternal}
            onClick={() => handleToggleHidden(item, !item.isHidden)}
            aria-label={
              item.isHidden
                ? t('tenant.dictionaries.detail.unhideItem')
                : t('tenant.dictionaries.detail.hideItem')
            }
            title={isExternal ? t('tenant.dictionaries.detail.externalReadonlyHint') : undefined}
            className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-muted"
            data-testid={`dictionary-item-toggle-hidden-${item.code}`}
          >
            {item.isHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          </button>
        )}
        {((canEditItems && item.origin === 'local') ||
          (canManage && item.origin === 'extension' && !isExternal)) &&
          item.id != null && (
            <>
              <button
                type="button"
                onClick={() => {
                  setItemServerErrors({});
                  setEditingItem(item);
                }}
                aria-label={t('common.edit')}
                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                data-testid={`dictionary-item-edit-${item.code}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setPendingDeleteItem(item)}
                aria-label={t('common.delete')}
                className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                data-testid={`dictionary-item-delete-${item.code}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="dictionary-detail">
      <header className="mb-4 shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-text-primary">{dict.name}</h2>
              {originBadge(dict, t)}
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-text-tertiary">
              <code className="rounded bg-bg-secondary px-1.5 py-0.5">{dict.code}</code>
              {dict.description && <span>{dict.description}</span>}
            </div>
            {isGlobal && (
              <p
                className="mt-1 text-xs text-text-tertiary"
                data-testid={
                  isExternal ? 'dictionary-detail-external-hint' : 'dictionary-detail-global-hint'
                }
              >
                {isExternal
                  ? t('tenant.dictionaries.detail.externalReadonlyHint')
                  : t('tenant.dictionaries.detail.globalReadonlyHint')}
              </p>
            )}
            {isErp && (
              <p
                className="mt-1 text-xs text-text-tertiary"
                data-testid="dictionary-detail-erp-hint"
              >
                {t('tenant.dictionaries.detail.erpReadonlyHint')}
              </p>
            )}
            {isLocalSql && (
              <p
                className="mt-1 text-xs text-text-tertiary"
                data-testid="dictionary-detail-sql-hint"
              >
                {t('tenant.dictionaries.detail.sqlReadonlyHint')}
              </p>
            )}
          </div>
          {canEditItems && (
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setItemServerErrors({});
                setCreateItemOpen(true);
              }}
              data-testid="dictionary-detail-new-item"
              className="shrink-0 whitespace-nowrap"
            >
              {t('tenant.dictionaries.detail.newItem')}
            </Button>
          )}
          {canAddExtension && (
            <Button
              variant="secondary"
              leftIcon={<Plus className="h-4 w-4" />}
              disabled={isExternal}
              title={isExternal ? t('tenant.dictionaries.detail.externalReadonlyHint') : undefined}
              onClick={() => {
                setItemServerErrors({});
                setCreateItemOpen(true);
              }}
              data-testid="dictionary-detail-add-own-item"
              className="shrink-0 whitespace-nowrap"
            >
              {t('tenant.dictionaries.detail.addOwnItem')}
            </Button>
          )}
        </div>
      </header>

      {items.length === 0 ? (
        <Card padding="md">
          {dict.itemsUnavailableReason === 'PARAMS_REQUIRED' ? (
            <p
              className="text-sm text-text-tertiary"
              data-testid="dictionary-detail-params-required-hint"
            >
              {t('tenant.dictionaries.detail.paramsRequiredHint')}
            </p>
          ) : (
            <p className="text-sm text-text-tertiary">{t('tenant.dictionaries.detail.empty')}</p>
          )}
        </Card>
      ) : (
        // Scroll pionowy NIEZALEŻNY od listy słowników po lewej — osobna ramka (Card),
        // ale `fillHeight` — patrz bliźniaczy komentarz w liście słowników (poziomy
        // pasek zawsze na dole widocznego panelu, nie na dole całej treści).
        // Tabela konfigurowalna — daje FILTR NAD KAŻDĄ KOLUMNĄ (wiersz filtrów pod
        // nagłówkiem) i sortowanie po kolumnach; przy słowniku typu „Państwa" (191 pozycji)
        // szukanie po jednym polu było za grube. Dane są klienckie, więc tabela filtruje
        // je lokalnie (brak zewnętrznych uchwytów filtrów = tryb lokalny).
        // `persistConfig={false}` — to ekran ustawień, nie lista kartoteki: nie zapisujemy
        // layoutu kolumn na serwerze i nie zaśmiecamy przestrzeni identyfikatorów tabel.
        <div className="flex min-h-0 flex-1 flex-col">
          <ConfigurableTable<DictionaryItem>
            tableId="tenant-dictionary-items"
            data={items}
            getRowId={(item) => item.code}
            columns={itemColumns}
            filterable
            configurable={false}
            persistConfig={false}
            showViewModeToggle={false}
            stickyHeader
            compact
            fillHeight
            // Wiersze dostają `dictionary-item-row-<kod>` (kotwica dla testów i smoke).
            testIdPrefix="dictionary-item"
            emptyTitle={t('tenant.dictionaries.detail.noMatches')}
            renderActions={renderItemActions}
            actionsColumnWidth={96}
          />
        </div>
      )}

      {(canEditItems || canAddExtension) && (
        <DictionaryItemFormModal
          open={createItemOpen || editingItem !== null}
          mode={editingItem ? 'edit' : 'create'}
          initialValues={itemFormInitialValues}
          serverErrors={itemServerErrors}
          // Wartość liczbowa istnieje tylko dla pozycji słowników LOKALNYCH — pozycje
          // globalne i rozszerzenia (słownik globalny) nie mają kolumny `value`.
          valueApplicable={!isGlobal}
          saving={
            mutations.createItem.isPending ||
            mutations.updateItem.isPending ||
            mutations.updateExtension.isPending
          }
          onSubmit={(values) => {
            if (editingItem && editingItem.id) {
              if (editingItem.origin === 'extension') {
                handleUpdateExtension(editingItem.id, values);
              } else {
                handleUpdateItem(editingItem.id, values);
              }
            } else {
              handleCreateItem(values);
            }
          }}
          onClose={() => {
            setCreateItemOpen(false);
            setEditingItem(null);
            setItemServerErrors({});
          }}
        />
      )}

      <ConfirmDialog
        open={pendingDeleteItem !== null}
        title={t('tenant.dictionaries.confirmDeleteItem.title')}
        message={
          pendingDeleteItem
            ? t('tenant.dictionaries.confirmDeleteItem.message', {
                label: pendingDeleteItem.label,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        onConfirm={() => {
          if (!pendingDeleteItem?.id) return;
          if (pendingDeleteItem.origin === 'extension') {
            handleDeleteExtension(pendingDeleteItem.id);
          } else {
            handleDeleteItem(pendingDeleteItem.id);
          }
        }}
        onClose={() => setPendingDeleteItem(null)}
      />
    </div>
  );
}
